import {
  Alert,
  FullScreenError,
  Label,
  TextV2,
  SvgSingleNeutralBold,
  Dialog,
  SectionTitle,
  SvgHierarchy8Bold,
} from '@insights-ltd/design-library/components';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { useEffect, useState } from 'react';
import SelectGroup from 'components/SelectGroup/SelectGroup';
import {
  Group,
  GroupResponse,
  useGetAllGroups,
  useSetRoleForUsersInGroup,
} from 'api';
import { useAuth } from 'contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router';
import { SelectUsersFromGroup } from 'components/UserPicker/SelectUsersFromGroup';
import { getRoleDescription, isSuperAdmin } from 'utils/role';
import { Role, User } from 'types/types';
import RolePermissionV2 from 'pages/EditAccount/EditAccountForm/RolePermissionV2';
import { PermissionRole } from 'pages/AddUser/AddUserForm/types';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useRequestErrorContext } from 'components/RequestErrorDialog/RequestErrorProvider';

const StyledForm = styled('form')({
  width: '100%',
  flexGrow: 1,
});

type PermissionData = {
  selectedGroup: Group | null;
  selectedUsers: User[];
  role: PermissionRole;
};

const BulkSetPermissionsForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: allGroups, status: groupStatus } = useGetAllGroups({
    enabled: user?.permissions.Organisation_Group_ReadAll === 'Global',
  });

  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('groupId');
  const initialGroup: GroupResponse | null =
    (groupId && allGroups?.find((group) => group.id === groupId)) || null;

  const { handleSubmit, setValue, reset, watch, getValues } =
    useForm<PermissionData>({
      defaultValues: {
        selectedGroup: initialGroup,
        selectedUsers: [],
        role: 'STANDARD',
      },
    });

  const [selectedGroup, selectedUsers, role] = watch([
    'selectedGroup',
    'selectedUsers',
    'role',
  ]);

  useEffect(() => {
    if (initialGroup && initialGroup.id !== selectedGroup?.id) {
      reset({
        selectedGroup: initialGroup,
        selectedUsers: [],
        role: 'STANDARD',
      });
    }
  }, [selectedGroup, initialGroup, reset]);

  const queryClient = useQueryClient();
  const { mutate, isPending: isSaving } =
    useSetRoleForUsersInGroup(queryClient);

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const { openErrorModal } = useRequestErrorContext();

  const submitHandler = handleSubmit((formData: PermissionData) => {
    if (!showConfirmModal) {
      setShowConfirmModal(true);
      return;
    }
    mutate(
      {
        ...formData,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        selectedGroup: selectedGroup!,
      },
      {
        onSuccess: () => {
          navigate(`/groups/${selectedGroup?.id}?successfulRoleChange`);
        },
        onError: () => {
          setShowConfirmModal(false);
          openErrorModal();
        },
      },
    );
  });

  if (groupStatus === 'pending') {
    return <Alert severity="info">{t('ui.event-management.loading')}</Alert>;
  }
  if (groupStatus === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.organisation.error-loading')}
      />
    );
  }

  const userFilter = ({ role: userRole }: User) => !isSuperAdmin(userRole);

  const formId = 'bulk-set-permissions-form';
  const SelectGroupLabel = (
    <Label>{t('ui.event-management.users.invite.select-group.label')}</Label>
  );

  return (
    <StyledForm onSubmit={submitHandler} id={formId}>
      <Box
        sx={{
          mt: spacingSizeMap.L,
          mb: spacingSizeMap.L,
        }}
      >
        <SectionTitle
          icon={{ icon: <SvgHierarchy8Bold />, iconColour: 'orange' }}
          text={t('ui.event-management.bulk-set-user-permissions.group.label')}
        />
      </Box>
      <Box
        sx={{
          mb: (theme) => theme.spacing(spacingSizeMap.L),
        }}
      >
        <SelectGroup
          selectedGroup={selectedGroup}
          onSelect={(group) => {
            setValue('selectedGroup', group);
            setValue('selectedUsers', []);
          }}
          options={allGroups ?? []}
          selectedLabel={SelectGroupLabel}
          fieldLabel={SelectGroupLabel}
          actionType="change"
          disabled={Boolean(groupId)}
        />
      </Box>
      {Boolean(selectedGroup) && (
        <>
          <Box
            sx={{
              mt: spacingSizeMap.L,
              mb: spacingSizeMap.L,
            }}
          >
            <SectionTitle
              icon={{ icon: <SvgSingleNeutralBold />, iconColour: 'blue' }}
              text={t(
                'ui.event-management.bulk-set-user-permissions.select-users.label',
              )}
            />
          </Box>
          <Box
            sx={{
              mb: (theme) => theme.spacing(spacingSizeMap.L),
            }}
          >
            <TextV2
              color="textSecondary"
              sx={{
                marginBottom: '22px',
              }}
            >
              {t(
                'ui.event-management.bulk-set-user-permissions.select-users.advice',
              )}
            </TextV2>
            <SelectUsersFromGroup
              selectedUsers={selectedUsers}
              setSelectedUsers={(users) => setValue('selectedUsers', users)}
              groupId={selectedGroup?.id || ''}
              userFilter={userFilter}
              showRole={true}
            />
          </Box>
          {Boolean(selectedUsers?.length) && (
            <>
              <RolePermissionV2
                permissionRole={role}
                changeRoleType={(value) => setValue('role', value)}
                isSuperAdmin={false}
                roleChangeIsPermitted={true}
                disableGMRoleChangeDialog={true}
              />
              <Grid
                container
                sx={{
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSaving}
                  endIcon={isSaving && <CircularProgress size={24} />}
                >
                  {t('ui.event-management.bulk-set-user-permissions.button')}
                </Button>
              </Grid>
            </>
          )}
        </>
      )}
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        content={
          (
            <>
              <Box
                sx={{
                  mb: (theme) => theme.spacing(spacingSizeMap.S),
                }}
              >
                <Trans
                  i18nKey="ui.event-management.bulk-set-user-permissions.confirm.body"
                  values={{
                    count: selectedUsers.length,
                    role: t(getRoleDescription(role as Role)),
                  }}
                  components={{
                    stress: <TextV2 component="span" variant="bodyBold" />,
                  }}
                />
              </Box>
              <Alert severity="warning">
                {t(
                  getValues().role === 'GROUP_MANAGER'
                    ? 'ui.event-management.users.invite.permission-roles.group-manager.modal.warning'
                    : 'ui.event-management.bulk-set-user-permissions.confirm.warning',
                )}
              </Alert>
            </>
          ) as any
        }
        title={t(
          'ui.event-management.bulk-set-user-permissions.confirm.heading',
        )}
        primaryButtonText={t(
          'ui.event-management.bulk-set-user-permissions.confirm.button.yes',
        )}
        secondaryButtonText={t(
          'ui.event-management.bulk-set-user-permissions.confirm.button.no',
        )}
        disablePrimaryButton={isSaving}
        onSubmit={() => {
          submitHandler();
        }}
      />
    </StyledForm>
  );
};

export default BulkSetPermissionsForm;
